import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Check, AlertCircle, Clock } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { useClasseContext } from '../context/ClasseContext';
import ModalSynchroGar from '../components/VueSynchronisationGar/ModalSynchroGar';
import ModalDesyncGar from '../components/VueSynchronisationGar/ModalDesyncGar';

function SynchronisationGAR({ classeDetail }) {
    const navigate = useNavigate();
    const { classeNom, groupNom } = useClasseContext();
    const { anneeScolaire } = useUser();

    const [showSyncModal, setShowSyncModal] = useState(false);
    const [showDesyncModal, setShowDesyncModal] = useState(false);
    const [selectedSyncStudent, setSelectedSyncStudent] = useState(null);
    const [students, setStudents] = useState({
        synchronized: [],
        pending: [],
        notConnected: [],
        newFromGAR: [],
    });

    // ─── Calcul de similarité (Levenshtein simplifié) ───
    const calculateSimilarity = useCallback((str1, str2) => {
        if (!str1 || !str2) return 0;
        const normalize = (str) => str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
        const s1 = normalize(str1);
        const s2 = normalize(str2);
        const maxLen = Math.max(s1.length, s2.length);
        if (maxLen === 0) return 100;
        let distance = 0;
        for (let i = 0; i < maxLen; i++) {
            if (s1[i] !== s2[i]) distance++;
        }
        return Math.round(((maxLen - distance) / maxLen) * 100);
    }, []);

    // ─── Correspondances GAR ↔ élèves existants ───
    // Fiche unique (spec GAR §0.1) : plus de filtre « mes élèves » — les fiches
    // appartiennent à l'établissement, tout prof de la classe peut arbitrer le
    // guichet. (L'ancien filtre par matiere/id_prof_createur se viderait de
    // toute façon après la bascule : ces champs deviennent null.)
    const findCorrespondances = useCallback((elevesGar, elevesExistants) => {
        const correspondances = [];
        const elevesGarSansCorrespondance = [];

        elevesGar.forEach(eleveGar => {
            let bestMatch = null;
            let bestScore = 0;

            elevesExistants.forEach(eleve => {
                const sameClasse = !groupNom || eleve.classe_nom === eleveGar.classe_nom;

                if (!eleve.synchronise_gar && sameClasse) {
                    const nomScore = calculateSimilarity(eleveGar.nom, eleve.nom);
                    const prenomScore = calculateSimilarity(eleveGar.prenom, eleve.prenom);
                    const totalScore = (nomScore + prenomScore) / 2;
                    if (totalScore > bestScore) {
                        bestScore = totalScore;
                        bestMatch = eleve;
                    }
                }
            });

            if (bestScore >= 80 && bestMatch) {
                correspondances.push({ eleveGar, eleveExistant: bestMatch, similarity: bestScore });
            } else {
                elevesGarSansCorrespondance.push(eleveGar);
            }
        });

        return { correspondances, elevesGarSansCorrespondance };
    }, [calculateSimilarity, groupNom]);

    // ─── Construction des catégories d'élèves ───
    useEffect(() => {
        if (!classeDetail?.eleves) {
            setStudents({ synchronized: [], pending: [], notConnected: [], newFromGAR: [] });
            return;
        }

        const synchronized = [];
        const notConnected = [];

        classeDetail.eleves.forEach(eleve => {
            if (eleve.synchronise_gar) {
                synchronized.push({
                    id: eleve.id,
                    nom: eleve.eleve_gar_nom,
                    prenom: eleve.eleve_gar_prenom,
                    eleve_temp_nom: eleve.nom,
                    eleve_temp_prenom: eleve.prenom,
                    initials: `${eleve.prenom[0]}${eleve.nom[0]}`.toUpperCase(),
                    garId: eleve.eleve_gar,
                    user: eleve.user,
                    resultats: eleve.resultats,
                    classe_nom: eleve.classe_nom,
                });
            } else {
                notConnected.push({
                    id: eleve.id,
                    nom: eleve.nom,
                    prenom: eleve.prenom,
                    initials: `${eleve.prenom[0]}${eleve.nom[0]}`.toUpperCase(),
                    user: eleve.user,
                    classe_nom: eleve.classe_nom,
                });
            }
        });

        // Comptes ENT DÉJÀ liés à une fiche synchronisée (fiche.eleve_gar === gar_details.id) :
        // on les exclut de la détection, sinon — n'ayant plus de fiche NON synchronisée à
        // matcher — ils retombent à tort dans « à synchroniser » et l'élève apparaît dans
        // les DEUX listes (synchronisés + à synchroniser).
        const garIdsLies = new Set(
            classeDetail.eleves.filter(e => e.synchronise_gar).map(e => e.eleve_gar),
        );
        const garDetailsNonLies = (classeDetail.eleves_gar_details || [])
            .filter(g => !garIdsLies.has(g.id));

        const { correspondances, elevesGarSansCorrespondance } = findCorrespondances(
            garDetailsNonLies,
            classeDetail.eleves || [],
        );

        const pending = correspondances.map(corr => ({
            id: corr.eleveExistant.id,
            nom: corr.eleveExistant.nom,
            prenom: corr.eleveExistant.prenom,
            initials: `${corr.eleveExistant.prenom[0]}${corr.eleveExistant.nom[0]}`.toUpperCase(),
            garName: `${corr.eleveGar.prenom} ${corr.eleveGar.nom}`,
            garId: corr.eleveGar.id_gar,
            garEleveId: corr.eleveGar.id,
            similarity: corr.similarity,
            tempId: corr.eleveExistant.id,
            classe_nom: corr.eleveGar.classe_nom,
        }));

        const newFromGAR = elevesGarSansCorrespondance.map(eleveGar => ({
            garId: eleveGar.id_gar,
            garEleveId: eleveGar.id,
            nom: eleveGar.nom,
            prenom: eleveGar.prenom,
            initials: `${eleveGar.prenom[0]}${eleveGar.nom[0]}`.toUpperCase(),
            username: eleveGar.username,
            classe_nom: eleveGar.classe_nom,
        }));

        setStudents({ synchronized, pending, notConnected, newFromGAR });
    }, [classeDetail, findCorrespondances, groupNom]);

    const handleClose = () => {
        setShowSyncModal(false);
        setShowDesyncModal(false);
    };

    const toSyncCount = students.pending.length + students.newFromGAR.length;

    return (
        <div className="w-full p-4 md:p-8">
            <div className="w-full mx-auto">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-400 to-cyan-400 rounded-t-3xl p-8 text-white text-center shadow-xl">
                    <h1 className="text-4xl font-light mb-3">{groupNom || classeNom || 'Classe'}</h1>
                    <div className="inline-flex items-center bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full">
                        <span className="text-lg">Année scolaire {anneeScolaire} • Synchronisation GAR</span>
                    </div>
                </div>

                {/* Stats */}
                <div className="bg-gray-50 p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="bg-white p-5 rounded-2xl shadow-md">
                            <div className="text-3xl font-bold text-green-500">{students.synchronized.length}</div>
                            <div className="text-gray-500 text-sm">Synchronisés</div>
                        </div>
                        <div className="bg-white p-5 rounded-2xl shadow-md">
                            <div className="text-3xl font-bold text-orange-500">{toSyncCount}</div>
                            <div className="text-gray-500 text-sm">À synchroniser</div>
                        </div>
                        <div className="bg-white p-5 rounded-2xl shadow-md">
                            <div className="text-3xl font-bold text-gray-400">{students.notConnected.length}</div>
                            <div className="text-gray-500 text-sm">En attente GAR</div>
                        </div>
                    </div>

                    {/* Bannière sync */}
                    {toSyncCount > 0 && (
                        <div className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-300 rounded-xl p-5 mt-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <AlertCircle className="text-orange-500" size={22} />
                                    <div>
                                        <div className="font-semibold text-gray-800">
                                            {toSyncCount} élève{toSyncCount > 1 ? 's' : ''} à synchroniser
                                        </div>
                                        <div className="text-sm text-gray-500 mt-0.5">
                                            {students.pending.length > 0 && `${students.pending.length} correspondance${students.pending.length > 1 ? 's' : ''}`}
                                            {students.pending.length > 0 && students.newFromGAR.length > 0 && ' · '}
                                            {students.newFromGAR.length > 0 && `${students.newFromGAR.length} nouveau${students.newFromGAR.length > 1 ? 'x' : ''}`}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowSyncModal(true)}
                                    className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-2.5 rounded-lg hover:from-green-600 hover:to-green-700 font-medium shadow-md transition-all"
                                >
                                    Synchroniser
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Élèves synchronisés */}
                <div className="bg-white p-6 rounded-b-3xl">
                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-3">
                        <Users className="text-blue-500" size={22} />
                        Mes élèves
                    </h2>

                    {students.synchronized.length === 0 && students.notConnected.length === 0 && (
                        <p className="text-gray-400 text-sm py-8 text-center">Aucun élève dans votre liste</p>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {students.synchronized.map(student => (
                            <div
                                key={student.id}
                                onClick={() => { setSelectedSyncStudent(student); setShowDesyncModal(true); }}
                                className="bg-white p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm">
                                        {student.initials}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="font-semibold text-gray-800 text-sm truncate">{student.prenom} {student.nom}</h4>
                                        <span className="inline-flex items-center gap-1 text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                                            <Check size={10} />
                                            Synchronisé
                                        </span>
                                        {groupNom && student.classe_nom && (
                                            <span className="inline-flex text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full ml-1">
                                                {student.classe_nom}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="text-[10px] text-gray-400 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    Cliquer pour gérer
                                </div>
                            </div>
                        ))}

                        {/* Élèves en attente — pills compactes */}
                        {students.notConnected.length > 0 && (
                            <div className="col-span-full mt-2">
                                <p className="text-xs text-gray-400 mb-2 flex items-center gap-1.5">
                                    <Clock size={12} />
                                    {students.notConnected.length} élève{students.notConnected.length > 1 ? 's' : ''} en attente de connexion GAR
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                    {students.notConnected.map(s => (
                                        <span key={s.id} className="text-xs bg-gray-100 text-gray-500 px-3 py-1.5 rounded-lg">
                                            {s.prenom} {s.nom?.[0]}.
                                            {groupNom && s.classe_nom && <span className="text-gray-400 ml-1">({s.classe_nom})</span>}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modals */}
            <ModalSynchroGar show={showSyncModal} handleClose={handleClose} students={students} groupNom={groupNom} />
            <ModalDesyncGar
                show={showDesyncModal}
                handleClose={() => setShowDesyncModal(false)}
                student={selectedSyncStudent}
                onDesyncSuccess={() => navigate(0)}
            />
        </div>
    );
}

export default SynchronisationGAR;
